import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Code2, FileText, Loader2, Play, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getCourseExplain, joinExplainNarrationScript } from '@/lib/scriptAudioAlignCourse';
import { artifactCategoryLabel, artifactLabel } from '@/lib/scriptAudioAlignArtifacts';
import { VisualLayer } from '@/components/scriptAudioAlign/courseArtifactRenderers';

const POLL_MS = 2000;

function UnitStatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  if (status === 'running') return <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />;
  return <span className="w-3.5 h-3.5 rounded-full border border-slate-500 shrink-0" />;
}

function ArtifactCard({ artifact }) {
  const category = artifact.category || artifactCategoryLabel(artifact.visual_type);
  const label = artifact.label || artifactLabel(artifact.visual_type);
  const spoken = String(artifact.narration_script || '').trim();
  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-950/40 overflow-hidden">
      <div className="px-4 py-2 border-b border-white/5 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-violet-300 font-medium">{category}</span>
        <span className="text-slate-500">→</span>
        <span className="text-slate-200">{label}</span>
        {artifact.rationale ? (
          <span className="text-slate-500 ml-auto truncate max-w-[40%]" title={artifact.rationale}>
            {artifact.rationale}
          </span>
        ) : null}
      </div>
      <div className="min-h-[80px]">
        <VisualLayer
          element={{
            visual_type: artifact.visual_type,
            payload: artifact.payload || {},
          }}
        />
      </div>
      {spoken ? (
        <div className="px-4 py-2.5 border-t border-white/5 bg-slate-900/40 space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-cyan-400/80 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Artifact narration
          </p>
          <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{spoken}</p>
        </div>
      ) : null}
    </div>
  );
}

function SubtopicCard({ unit, index }) {
  const pending = unit.status === 'pending';
  const running = unit.status === 'running';
  const topic = String(unit.topic_title || '').trim();
  const subtopic = String(unit.subtopic || '').trim();
  let unitKind = String(unit.unit_kind || '').trim().toLowerCase();
  if (!unitKind) {
    unitKind = topic && (!subtopic || subtopic.toLowerCase() === topic.toLowerCase())
      ? 'topic'
      : 'subtopic';
  }
  const isTopic = unitKind === 'topic';
  const title = isTopic ? (topic || 'Topic overview') : (subtopic || topic || 'Subtopic');
  return (
    <section
      className={cn(
        'rounded-2xl border overflow-hidden',
        running && 'border-amber-500/30',
        unit.status === 'completed' && 'border-emerald-500/20',
        unit.status === 'failed' && 'border-red-500/30',
      )}
      style={!running && unit.status !== 'completed' && unit.status !== 'failed' ? { borderColor: 'var(--glass-border)' } : undefined}
    >
      <header className="flex items-center gap-2 px-4 py-3 bg-slate-900/30 border-b border-white/5">
        <UnitStatusIcon status={unit.status} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 truncate">
            {isTopic ? 'Topic overview' : topic}
          </p>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">
            {title}
          </h3>
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0 capitalize">
          {isTopic ? 'topic' : 'subtopic'}
        </Badge>
        <Badge variant="outline" className="text-[10px] shrink-0">
          #{index + 1}
        </Badge>
      </header>
      <div className="p-4 space-y-3">
        {pending ? (
          <p className="text-sm text-slate-500">Waiting in queue…</p>
        ) : null}
        {running ? (
          <p className="text-sm text-amber-300/90 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating artifacts & narration…
          </p>
        ) : null}
        {unit.error ? (
          <p className="text-sm text-red-300">{unit.error}</p>
        ) : null}
        {(unit.artifacts || []).map((art, i) => (
          <ArtifactCard key={`${art.visual_type}-${i}`} artifact={art} />
        ))}
        {unit.overview_script ? (
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-950/20 p-3 space-y-1.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-cyan-400/80">
              <FileText className="w-3 h-3" />
              {isTopic ? 'Topic overview narration' : 'Subtopic overview narration'}
            </div>
            <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
              {unit.overview_script}
            </p>
          </div>
        ) : null}
        {unit.narration_script ? (
          <div className="rounded-xl border border-slate-700/50 bg-slate-950/50 p-3 space-y-1.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500">
              <FileText className="w-3 h-3" />
              Full unit narration (overview + artifacts)
            </div>
            <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
              {unit.narration_script}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function CourseExplainPanel({
  jobId,
  onJobUpdate,
  onCreatePreview,
  onUseScript,
  previewLoading = false,
}) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  const poll = useCallback(async () => {
    if (!jobId) return null;
    try {
      const data = await getCourseExplain(jobId);
      setJob(data);
      setError(null);
      onJobUpdate?.(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load explain job');
      return null;
    }
  }, [jobId, onJobUpdate]);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      return undefined;
    }
    let cancelled = false;
    let timer;

    const tick = async () => {
      const data = await poll();
      if (cancelled) return;
      if (data?.status === 'completed' || data?.status === 'failed') return;
      timer = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, poll]);

  if (!jobId) return null;

  if (error && !job) {
    return <p className="text-sm text-red-400">{error}</p>;
  }

  if (!job) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading artifact generation…
      </div>
    );
  }

  const total = job.total_units || 0;
  const done = job.completed_units || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const units = Array.isArray(job.units) ? job.units : [];
  const fullScript = joinExplainNarrationScript(job).trim()
    || (job.generated_script || '').trim()
    || units.map((u) => u.narration_script).filter(Boolean).join('\n\n').trim();
  const canPreview = job.status === 'completed'
    && units.some((u) => u.status === 'completed' && (u.artifacts || []).length)
    && Boolean(fullScript);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Lesson artifacts (per topic & subtopic)</h2>
        <Badge variant="outline" className="text-[10px] capitalize">
          {job.status || 'pending'}
        </Badge>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-400">
          <span>{done} / {total} units</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-500',
              job.status === 'failed' ? 'bg-red-500/70' : 'bg-violet-500/70',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {job.error_message ? (
        <p className="text-sm text-red-300">{job.error_message}</p>
      ) : null}

      <div className="space-y-4">
        {units.map((unit, i) => (
          <SubtopicCard key={`${unit.unit_kind || 'u'}-${unit.topic_title}-${unit.subtopic}-${i}`} unit={unit} index={i} />
        ))}
      </div>

      {job.status === 'completed' && fullScript ? (
        <section className="rounded-2xl border p-4 space-y-3" style={{ borderColor: 'var(--glass-border)' }}>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Compose narration script
            </h3>
            <Badge variant="outline" className="text-[10px]">Per topic & subtopic → joined</Badge>
          </div>
          <p className="text-xs text-slate-500">
            Each topic overview and subtopic script was written after its lesson artifacts finished, then joined for alignment.
          </p>
          <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto rounded-lg border border-slate-700/50 bg-slate-950/40 p-3">
            {fullScript}
          </pre>
          <div className="flex flex-wrap gap-2">
            {onUseScript ? (
              <Button type="button" size="sm" variant="outline" onClick={() => onUseScript(fullScript)}>
                <Code2 className="w-4 h-4 mr-1.5" />
                Use this script for alignment
              </Button>
            ) : null}
            {canPreview ? (
              <Button
                type="button"
                size="sm"
                onClick={() => onCreatePreview?.(job)}
                disabled={previewLoading || !onCreatePreview}
              >
                {previewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {previewLoading ? 'Creating render preview…' : 'Create render preview'}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
